import { PBI_URLS } from "../apiConfig.js";
import apiClient from "../apiInstance.js";

// ── Helper ────────────────────────────────────────────────────────
async function get(url, params) {
  try {
    const response = await apiClient.get(url, { params });
    return response.data;
  } catch (err) {
    console.error(`Error fetching ${url}`, err?.response?.status, err?.response?.data || err?.message);
    throw err;
  }
}


// ── Campaigns ─────────────────────────────────────────────────────
export const getDashboardCampaigns = () => get(PBI_URLS.DASHBOARD_CAMPAIGNS);


// ── Scorecard (MTD tab) ───────────────────────────────────────────
// Twelve day-matched periods in three groups — year, month, week — from the
// SAME query the scheduled Summary report runs. No params: the periods are
// derived from today in the tenant's zone, server-side.
//
// Optional { date: "YYYY-MM-DD" } overrides the anchor, for reproducing a
// past report run or checking a month boundary without waiting for one.
//
// Path is a literal rather than a PBI_URLS entry only to keep this addition to
// one file. Move it to apiConfig alongside the others when convenient — the
// hourly routes there are already bare relative paths, so it will look the
// same either way.
const DASHBOARD_SCORECARD = "/dashboard/scorecard";
export const getDashboardScorecard = (params) => get(DASHBOARD_SCORECARD, params);


// ── Trends ────────────────────────────────────────────────────────
export const getDashboardTexts   = (params) => get(PBI_URLS.DASHBOARD_TEXTS,   params);
export const getDashboardTiming  = (params) => get(PBI_URLS.DASHBOARD_TIMING,  params);
export const getDashboardOutcome = (params) => get(PBI_URLS.DASHBOARD_OUTCOME, params);
export const getDashboardHeatmap = (params) => get(PBI_URLS.DASHBOARD_HEATMAP, params);


// ── DPD ───────────────────────────────────────────────────────────
export const getDashboardPastdue = (params) => get(PBI_URLS.DASHBOARD_PASTDUE, params);

export const getDashboardDpdOutcomes = (params) => get(PBI_URLS.DASHBOARD_DPD_OUTCOMES, params);
export const getDashboardDpdTiming   = (params) => get(PBI_URLS.DASHBOARD_DPD_TIMING,   params);
export const getDashboardDpdHeatmap  = (params) => get(PBI_URLS.DASHBOARD_DPD_HEATMAP,  params);
export const getDashboardDpdResponses  = (params) => get(PBI_URLS.DASHBOARD_DPD_RESPONSES,  params);

// ── Opt-outs ──────────────────────────────────────────────────────
export const getDashboardOptouts        = (params) => get(PBI_URLS.DASHBOARD_OPTOUTS,         params);
export const getDashboardOptoutsByDpd   = (params) => get(PBI_URLS.DASHBOARD_OPTOUTS_BY_DPD,  params);
export const getDashboardOptoutsHeatmap = (params) => get(PBI_URLS.DASHBOARD_OPTOUTS_HEATMAP, params);
export const getDashboardOptoutsSummary = (params) => get(PBI_URLS.DASHBOARD_OPTOUTS_SUMMARY, params);


// ── Language & Transfers ──────────────────────────────────────────
export const getDashboardLanguage  = (params) => get(PBI_URLS.DASHBOARD_LANGUAGE,  params);
export const getDashboardTransfers = (params) => get(PBI_URLS.DASHBOARD_TRANSFERS, params);


// ── Payments & Promises ───────────────────────────────────────────
export const getDashboardPaymentsSummary     = (params) => get(PBI_URLS.DASHBOARD_PAYMENTS_SUMMARY,      params);
export const getDashboardPromisesFulfillment = (params) => get(PBI_URLS.DASHBOARD_PROMISES_FULFILLMENT,  params);


// ── PTP ───────────────────────────────────────────────────────────
export const getDashboardPtpSummary = (params) => get(PBI_URLS.DASHBOARD_PTP_SUMMARY, params);
export const getDashboardPtpByDpd   = (params) => get(PBI_URLS.DASHBOARD_PTP_BY_DPD,  params);
export const getDashboardPtpByDay   = (params) => get(PBI_URLS.DASHBOARD_PTP_BY_DAY,  params);
export const getDashboardPtpByDpdDay = (params) => get(PBI_URLS.DASHBOARD_PTP_BY_DPD_DAY, params);

// ── Abandonment ───────────────────────────────────────────────────
export const getDashboardAbandonment       = (params) => get(PBI_URLS.DASHBOARD_ABANDONMENT,        params);
export const getDashboardAbandonmentStages = (params) => get(PBI_URLS.DASHBOARD_ABANDONMENT_STAGES, params);


// ── Sentiment ─────────────────────────────────────────────────────
export const getDashboardSentimentPolarity = (params) => get(PBI_URLS.DASHBOARD_SENTIMENT_POLARITY, params);
export const getDashboardSentimentEmotion  = (params) => get(PBI_URLS.DASHBOARD_SENTIMENT_EMOTION,  params);
export const getDashboardSentimentScore    = (params) => get(PBI_URLS.DASHBOARD_SENTIMENT_SCORE,    params);
export const getDashboardSentimentWilling  = (params) => get(PBI_URLS.DASHBOARD_SENTIMENT_WILLING,  params);


// ── Compliance ────────────────────────────────────────────────────
export const getDashboardComplianceSummary  = (params) => get(PBI_URLS.DASHBOARD_COMPLIANCE_SUMMARY,   params);
export const getDashboardComplianceByRule   = (params) => get(PBI_URLS.DASHBOARD_COMPLIANCE_BY_RULE,   params);
export const getDashboardComplianceMtd      = (params) => get(PBI_URLS.DASHBOARD_COMPLIANCE_MTD,       params);

export const getPtpTargetsBucket = async () => {
  const { data } = await apiClient.get(PBI_URLS.DASHBOARD_PTP_TARGETS_BUCKET);
  return data;
};
 
// payload: { rows: [{ dpd_bucket, rpc_max, rpc_min, ptp_max, ptp_min }, ...] }
export const savePtpTargetsBucket = async (payload) => {
  const { data } = await apiClient.post(PBI_URLS.DASHBOARD_PTP_TARGETS_BUCKET, payload);
  return data;
};

// ── Campaign comparison ───────────────────────────────────────────
export const getDashboardCompareTexts    = (params) => get(PBI_URLS.DASHBOARD_COMPARE_TEXTS,    params);
export const getDashboardComparePtp      = (params) => get(PBI_URLS.DASHBOARD_COMPARE_PTP,      params);
export const getDashboardComparePayments = (params) => get(PBI_URLS.DASHBOARD_COMPARE_PAYMENTS, params);

// ── Cure-Aging ────────────────────────────────────────────────────
export const getDashboardCureAgingSummary = (params) => get(PBI_URLS.DASHBOARD_CURE_AGING_SUMMARY, params);
export const getDashboardCureAgingByDpd   = (params) => get(PBI_URLS.DASHBOARD_CURE_AGING_BY_DPD,   params);
export const getDashboardCureAgingByDay   = (params) => get(PBI_URLS.DASHBOARD_CURE_AGING_BY_DAY,   params);
export const getDashboardCureAgingTiming = (params) => get(PBI_URLS.DASHBOARD_CURE_AGING_TIMING, params);

export const getDashboardDelinquencyByDay = (params) => get(PBI_URLS.DASHBOARD_DELINQUENCY_BY_DAY, params);
export const getDashboardDelinquencyByDpd = (params) => get(PBI_URLS.DASHBOARD_DELINQUENCY_BY_DPD, params);
// pbiservices.js
export const getDashboardDelinquencyUnique = (params) => get(PBI_URLS.DASHBOARD_DELINQUENCY_UNIQUE, params);

// ── 24-hr Pay ─────────────────────────────────────────────────────
export const get24hrPay = (params) => get(PBI_URLS.DASHBOARD_24HR_PAY, params);
 
// { date, ids:[...] } → { date, count, amount, requested, matched }
export const calc24hrPayForDate = async ({ date, ids }) => {
  const { data } = await apiClient.post(PBI_URLS.DASHBOARD_24HR_PAY_CALCULATE, { date, ids });
  return data;
};
 
// rows: [{ date, count, amount, source?, ids? }] → upsert by date
export const save24hrPay = async (rows) => {
  const { data } = await apiClient.post(PBI_URLS.DASHBOARD_24HR_PAY, { rows });
  return data;
};
 
export const delete24hrPay = async (date) => {
  const { data } = await apiClient.delete(`${PBI_URLS.DASHBOARD_24HR_PAY}/${date}`);
  return data;
};

// ── PTP / Trends — hourly, client-zone ───────────────────────────
// Same params shape as the daily versions above ({ startDate, endDate,
// campaign, granularity, channel }). The tenant zone is resolved server-side
// from clientconfig — nothing extra to pass.
export const getDashboardPtpSummaryHourly    = (params) => get(PBI_URLS.DASHBOARD_PTP_SUMMARY_HOURLY,    params);
export const getDashboardPtpByDpdHourly      = (params) => get(PBI_URLS.DASHBOARD_PTP_BY_DPD_HOURLY,     params);
export const getDashboardPtpByDayHourly      = (params) => get(PBI_URLS.DASHBOARD_PTP_BY_DAY_HOURLY,     params);
export const getDashboardPtpByDpdDayHourly   = (params) => get(PBI_URLS.DASHBOARD_PTP_BY_DPD_DAY_HOURLY, params);
export const getDashboardHeatmapHourly       = (params) => get(PBI_URLS.DASHBOARD_HEATMAP_HOURLY,        params);
export const getDashboardOutcomeHourly       = (params) => get(PBI_URLS.DASHBOARD_OUTCOME_HOURLY,        params);
export const getDashboardTimingHourly        = (params) => get(PBI_URLS.DASHBOARD_TIMING_HOURLY,         params);

// Distinct Accountid in customerconvevent over the window. Its own call
// rather than a field on ptp-summary: the distinct scan is materially more
// expensive than the summary read, and the funnels want it only for the
// current period — never for the prev-period delta fetch.
export const getDashboardAccountsHourly = (params) => get(PBI_URLS.DASHBOARD_ACCOUNTS_HOURLY, params);